import { api } from '@/lib/api';
import type { PatientStatus } from '@valiosascrm/shared';

export type { PatientStatus } from '@valiosascrm/shared';

export interface Patient {
  id: string;
  name: string;
  // CPF is stored encrypted by the API with cpfHash for lookups; frontend only receives the response DTO.
  cpf: string | null;
  phone: string | null;
  // Story 61.4 — WhatsApp rejeitou o número (pré-checagem ou falha permanente); NULL = válido/desconhecido.
  phoneInvalidAt?: string | null;
  phoneInvalidReason?: string | null;
  email: string | null;
  birthDate: string | null;
  status: PatientStatus;
  avatarUrl: string | null;
  gender: string | null;
  address: {
    street?: string;
    number?: string;
    complement?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  } | null;
  optOutMarketing: boolean;
  guardianPatientId?: string | null;
  guardian?: { id: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface PatientListResult {
  data: Patient[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PatientQueryParams {
  search?: string;
  status?: PatientStatus;
  // Advanced filters
  state?: string;
  city?: string;
  lastVisitDays?: number;
  inactivityDays?: number;
  minSpent?: number;
  maxSpent?: number;
  paymentStatus?: 'paid' | 'pending' | 'overdue';
  professionalId?: string;
  phoneInvalid?: boolean; // Story 61.4 AC7
  countOnly?: boolean;
  // Pagination
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface CreatePatientData {
  name: string;
  phone?: string;
  // CPF is transient form input; the API encrypts it and persists cpfHash.
  cpf?: string;
  email?: string;
  birthDate?: string;
  address?: Patient['address'];
  avatarUrl?: string | null;
  gender?: string;
  optOutMarketing?: boolean;
  guardianPatientId?: string | null;
}

export interface UpdatePatientData extends Partial<CreatePatientData> {
  status?: Patient['status'];
}

// Get patients with pagination and filtering
export async function getPatients(params: PatientQueryParams = {}): Promise<PatientListResult> {
  const { data } = await api.get<PatientListResult>('/patients', { params });
  return data;
}

// Get a single patient by ID
export async function getPatient(id: string): Promise<Patient> {
  const { data } = await api.get<Patient>(`/patients/${id}`);
  return data;
}

// Create a new patient
export async function createPatient(patientData: CreatePatientData): Promise<Patient> {
  const { data } = await api.post<Patient>('/patients', patientData);
  return data;
}

// Update a patient
export async function updatePatient(id: string, patientData: UpdatePatientData): Promise<Patient> {
  const { data } = await api.patch<Patient>(`/patients/${id}`, patientData);
  return data;
}

// Archive (soft delete) a patient
export async function archivePatient(id: string): Promise<{ message: string }> {
  const { data } = await api.delete<{ message: string }>(`/patients/${id}`);
  return data;
}

// Restore an archived patient
export async function restorePatient(id: string): Promise<{ message: string }> {
  const { data } = await api.post<{ message: string }>(`/patients/${id}/restore`);
  return data;
}

// Update patient avatar
export async function updatePatientAvatar(id: string, avatarUrl: string | null): Promise<{ avatarUrl: string | null }> {
  const { data } = await api.patch<{ avatarUrl: string | null }>(`/patients/${id}/avatar`, { avatarUrl });
  return data;
}

// Get patient count for filter preview
export async function getPatientCount(params: PatientQueryParams): Promise<number> {
  const { data } = await api.get<{ count: number }>('/patients/count', { params });
  return data.count;
}

// Export patients to CSV
export async function exportPatientsCsv(params: PatientQueryParams): Promise<Blob> {
  const response = await api.get('/patients/export', {
    params,
    responseType: 'blob',
  });
  return response.data;
}

export async function getPatientFilterSuggestions(
  field: 'city' | 'state',
  query = '',
  limit = 40,
): Promise<string[]> {
  const { data } = await api.get<{ suggestions: string[] }>('/patients/filter/suggestions', {
    params: { field, query, limit },
  });
  return data.suggestions;
}
